import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  Link,
  List,
  ListItem,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { AiOutlineCloudDownload } from 'react-icons/ai'
import Iframe from 'react-iframe'
import {
  DownloadPDFDriveLink,
  EmbedYoutubeVideo,
  StandardLinkToBlank,
  UnderscoredText,
} from '../../modules/guides/guideElements'

const CONTENT_ITEMS = [
  {
    id: 'introduction',
    headline: 'Introduction',
    content: (
      <>
        <b>PayLeaf</b> is a <b>new method of payment at dispensaries</b> and
        other retail locations, which allows you to make direct electronic
        payments with your phone,{' '}
        <b>with no transaction fees for the customer.</b> Currently, when you go
        to a dispensary, your options are limited to bringing cash with you,
        using a debit card with extremely high fees or taking out cash at an ATM
        that also charges excessive fees. PayLeaf has found a way to remove the
        middleman (credit card companies) from transactions, by creating a
        protocol that directly transfers customer funds electronically to
        merchants.
        <br></br>
        <br></br>
        This guide will show you how to start using PayLeaf to pay for goods without any fees for you.
        You can choose to read, download a PDF guide or watch a short video.
        <br></br>
        <br></br>
        To make payments with PayLeaf, you will only need two things:
        <br></br>
        <b>1) Phantom wallet</b> - an app on your phone
        <br></br>
        <b>2) Funding your Phantom wallet</b> with either USDC, SOL or PUFF
        <br></br>
        <br></br>
        You can make use PayLeaf with one of three "payment methods" / coins:
        <br></br>
        <b>
          -{' '}
          <StandardLinkToBlank href='https://www.circle.com/en/usdc'>
            USDC (US Dollar Coin)
          </StandardLinkToBlank>
        </b>{' '}
        - A digital dollar or so called “stablecoin” that is directly pegged to the US Dollar, issued by
        Circle. You can exchange US dollars on a 1:1 ratio for USDC. Please note
        that you can only use Solana based USDC, not ETH based USDC.
        <br></br>
        <b>
          -{' '}
          <StandardLinkToBlank href='https://www.solana.com'>
            SOL (Solana)
          </StandardLinkToBlank>
        </b>{' '}
        - the native token of a high-performance blockchain built to enable
        scalable, user-friendly, decentralized apps for the world.
        <br></br>
        <b>
          -{' '}
          <StandardLinkToBlank href='https://www.stonedapecrew.com'>
            PUFF
          </StandardLinkToBlank>
        </b>{' '}
        - the utility token of the Stoned Ape ecosystem - a well-known NFT
        project on Solana
        <br></br>
        <br></br>
        For individuals that already own cryptocurrencies, this process will be
        extremely easy. <br></br>Even if you have never bought crypto before,
        the process to get started can be accomplished in a few simple steps.
        {/*
          Pay in a fast and easy way in dispensaries using your mobile phone with PayLeaf - <b>no fees for you</b><br></br>
          You only need to setup a crypto wallet called Phantom and fund it with <Link href='https://www.circle.com/en/usdc' target='_blank' textDecoration={'underline'}>USDC, which is a digital dollar, also known as a stablecoin.</Link> It can be 1:1 exchanged for cash according to Circle.<br/>
          Once setup, it's super easy for you to use and you don't have any risk of volatility.
          All of this is operating on the Solana blockchain, so that's why you need Phantom.
      */}
      </>
    ),
  },
  {
    id: 'getting-started-with-phantom',
    headline: 'Getting started with Phantom',
    content: (
      <>
        What is Phantom? It is a digital wallet for Solana that keeps your coins
        safe electronically. Phantom makes it safe and easy for you to store,
        buy, send, receive, swap tokens, cryptocurrencies and collect NFTs on
        the Solana blockchain.
        <br></br>
        <br></br>
        We have created a simple video guide for you to create a Phantom wallet on
        your mobile device.
        <EmbedYoutubeVideo videoHref={'https://www.youtube.com/embed/WqsQsaOrKPo'}></EmbedYoutubeVideo>
        <br></br>
        <br></br>
        Alternatively, you can also follow the instructions in the PDF guide.<br/>
        <DownloadPDFDriveLink driveId='1GbJRS8e8AyJNuCC-4-Ay7TEzz8Xe0vst'>
          Download "Setup Phantom Mobile" Guide
        </DownloadPDFDriveLink>
      </>
    ),
  },
  {
    id: 'funding-your-wallet',
    headline: 'Funding your wallet with USDC',
    content: (
      <>
        There are a number of options to provide funding to your wallet.
        <br></br>
        If you are in a store and would like to sign up quickly to use PayLeaf,
        you can use one of the methods that will immediately provide you with
        the ability to add funding, using a credit card or a debit card. Please
        keep in mind that these quicker options will charge you up to a 3% fee,
        but will allow you to try it out in minutes.
        <br></br>
        There are much cheaper options to pursue, but these will take more time
        to setup. The cheapest would be using Kraken, where you can make deposits
        for FREE (once a week by ACH deposit, with other ACH deposits costing
        $0.50). See{' '}
        <StandardLinkToBlank
          href='https://kraken.com/'
        >
          kraken.com
        </StandardLinkToBlank>
        <br></br>
        <br></br>
        <UnderscoredText>How to Quickly Fund Your Wallet</UnderscoredText>
        <br></br>
        <b>With Credit Card</b> - You can use MoonPay to quickly fund your
        wallet by buying SOL (Solana) using your credit card. MoonPay is
        automatically linked to Phantom, so the process is relatively simple.
        See{' '}
        
        <StandardLinkToBlank
          href='https://www.moonpay.com'
        >
          moonpay.com
        </StandardLinkToBlank>
        <br></br>
        We have also prepared a short video on how to fund your wallet with MoonPay.
        <EmbedYoutubeVideo videoHref={'https://www.youtube.com/embed/lTC14LnTmRg'}></EmbedYoutubeVideo>
        <br></br>
        And if you want to remove the volatility of holding SOL, you can exchange it for USDC on jup.ag. You need to navigate to the browser in your Phantom wallet (last tab) and type in "jup.ag" in the searchbar. Then connect your wallet, click 'Max' and 'Swap', then approve the transaction.
        <br></br>
        <br></br>
        <b>Kraken with ACH deposits</b> will be your cheapest option.
        <br></br>
        <br></br>
        Here you can find a guide on how to sign up for Kraken in their app.
        <br></br>
        You can refer to the official Kraken documentation to get you started:{' '}
        <StandardLinkToBlank href='https://support.kraken.com/hc/en-us/articles/226090548-How-to-create-an-account-on-Kraken'>
          https://support.kraken.com/hc/en-us/articles/226090548-How-to-create-an-account-on-Kraken
        </StandardLinkToBlank>
        <br></br>
        <br></br>
        <UnderscoredText>How to Send USDC, SOL to your Phantom Wallet</UnderscoredText><br></br>
        For those who are already buying and selling crypto, it is quite simple to send SOL or USDC (again, this needs to be USDC on the Solana network) to your Phantom wallet from your crypto brokerage account.
        <br></br>And if you want to remove the volatility of holding SOL, you can exchange it for USDC.
        <br></br>

        <br></br>Alternatively, below are two examples of how to send SOL to your phantom wallet from:
        <br></br>
        From Coinbase:{' '}
        <DownloadPDFDriveLink driveId='1zgJd1D4uZZgMajIBxqMpzyk6wD8amNVG'>
          Download "Sending from Coinbase" Guide
        </DownloadPDFDriveLink>
        <br></br>
        <br></br>
        From Kraken:{' '}
        <StandardLinkToBlank href='https://support.kraken.com/hc/en-us/articles/360000672763-How-to-withdraw-cryptocurrencies-from-your-Kraken-account'>
        https://support.kraken.com/hc/en-us/articles/360000672763-How-to-withdraw-cryptocurrencies-from-your-Kraken-account
        </StandardLinkToBlank>
        
      </>
    ),
  },
  {
    id: 'making-a-payment',
    headline: 'Making a payment',
    content: (
      <>
        Now that you have funded your account, you are at the easy part.<br></br>
        All you need to do now is:<br></br>
        <b>1 - </b>tell the merchant you would like to use PayLeaf
        <br></br><b>2 - </b>tell him/her which currency you would like to use to pay (USDC, SOL or PUFF)
        <br></br><b>3 - </b>After this, you will enter the Phantom app, press QR Code symbol on top right, scan the QR code and submit payment with press 'Approve'.
        <br></br><br></br>It’s as simple as that. Below is a more detailed step-by-step guide.
        <br></br>
        <EmbedYoutubeVideo videoHref='https://www.youtube.com/embed/3fm4d7t2A40'></EmbedYoutubeVideo>
        <br></br>
        <DownloadPDFDriveLink driveId='1V6ZnEYHs_eZ0bgOXHc1T74S7j1pCX40Q'>
          Download "Making a payment step-by-step" Guide
        </DownloadPDFDriveLink>
        <br></br><br></br>
        Congrats, now you are all set and while the setup process takes a few minutes, you will find for further purchases that this is a much simpler way of making your cannabis payments. 
        <br></br>You will be able to save significant sums of money by using Kraken.
        <br></br><br></br>As we expand the PayLeaf networks, your benefits will increase and you will be able to receive various discounts and perks (such as free NFTs) sent directly to your wallet.  And you will never pay any transaction fees!
      </>
    ),
  },
]

const IntroductionForCustomers = () => {
  const router = useRouter()
  const [activeNavItem, setActiveNavItem] = useState(CONTENT_ITEMS[0].id)

  useEffect(() => {
    const handleScroll = (e: any) => {}
    document.addEventListener('scroll', (e) => {
      if (window.scrollY >= 0 && window.scrollY <= window.innerHeight / 2) {
        setActiveNavItem(CONTENT_ITEMS[0].id)
      } else {
        // const indexOfSmallestDifference =
        for (let i = 0; i < CONTENT_ITEMS.length; i++) {
          const activeElementId = CONTENT_ITEMS[i]?.id
          if (!activeElementId) continue
          const elementById = document.getElementById(activeElementId)

          if ((elementById?.getBoundingClientRect().top ?? 61) < 60) {
            setActiveNavItem(activeElementId)
          }
        }
      }
    })
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <Box
      minHeight={'100vh'}
      background='#F3F3F5'
      color='#262626'
      display={'flex'}
      flexDirection='column'
      justifyContent={'space-between'}
      scrollBehavior={'smooth'}
    >
      <Container maxW={'78.5rem'} paddingY='2rem' px='1rem'>
        <Link href='/guides'>
          <Button
            bg='white'
            border='none'
            fontSize='.75rem'
            mb='1rem'
            transition={'all 0.5s ease'}
            _hover={{
              transform: 'translateY(-5px)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.16)',
            }}
          >
            {'<'}- Back to overview
          </Button>
        </Link>
        <Heading mt='2rem' mb='2rem' fontFamily={'Qualy'}>
          Introduction for Customers
        </Heading>
        <Box
          display={['block', 'flex']}
          position='relative'
          transition={'all 0.5s ease'}
          _hover={{
            transform: 'translateY(-5px)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.16)',
          }}
          bg='white'
          borderRadius='10px'
          px={['1rem', '2rem']}
          py='2rem'
          color='#425466'
        >
          <Box>
            <Box position={['unset', 'sticky']} height={'auto'} top='5%' paddingY='1rem'>
              <List spacing={'1rem'}>
                {CONTENT_ITEMS.map((contentItem) => (
                  <ListItem
                  key={contentItem.id}
                    paddingX='1rem'
                    fontWeight={600}
                    fontSize='.875rem'
                    color={activeNavItem === contentItem.id ? 'green' : 'auto'}
                    borderLeft={
                      activeNavItem === contentItem.id
                        ? '1px solid green'
                        : 'none'
                    }
                  >
                    <Link href={'#' + contentItem.id}>
                      {contentItem.headline}
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
          <Box
            width={['100%', '75%']}
            paddingY='1rem'
            paddingX={['.4rem', '2rem']}
          >
            {CONTENT_ITEMS.map((contentItem, i) => (
              <Box
              key={contentItem.id}>
                <Text
                  mt={i == 0 ? 0 : '3rem'}
                  fontSize='1.2rem'
                  fontWeight={600}
                  id={contentItem.id}
                >
                  {contentItem.headline}
                </Text>
                <Text mt='1rem' fontSize='.9rem' lineHeight={'1.6rem'}>
                  {contentItem.content}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default IntroductionForCustomers
